import {
	ALERT_38,
	ALERT_39,
	FILE_SIZE_10MB,
	FILE_SIZE_10MB_BYTE,
	FileTypeEnum,
	MAX_FILE_UPLOAD,
	REGEX,
} from '@/constant';
import { useRowSelection } from '@/hooks';
import { uploadFile as uploadFileAPI } from '@/services/api';
import {
	convertAndCompressImages,
	getImageType,
	isHeicHeifFile,
	showAlert,
	validateAllowFile,
} from '@/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';

import {
	FileStatusEnum,
	getColumnsUpload,
} from '../define-document-upload.config';
import { formatLocalFile } from '../define-document-upload.helper';
import { LocalFile } from '../define-document-upload.type';

export const useUploadFileLogic = () => {
	const fileRowSelection = useRowSelection<any, LocalFile>({
		getCheckboxProps: (record: LocalFile) => ({
			disabled: record?.status !== FileStatusEnum.DONE,
		}),
	});

	const [localFiles, setLocalFile] = useState<LocalFile[]>([]);
	const [converting, setConverting] = useState(false);
	const [originalFileNames, setOriginalFileNames] = useState<
		Record<string, string>
	>({});

	const fileUploadedDataSource = useMemo(() => {
		return localFiles.map((f, index, arr) => ({
			...f,
			no: arr.length - index,
		}));
	}, [localFiles]);

	const uploadColumns = useMemo(() => getColumnsUpload(), []);
	const { t } = useTranslation('validation');

	const uploadSingleFile = useCallback(
		async (file: LocalFile) => {
			const fileId = file.id;

			// Set status to CONVERTING
			setLocalFile((prevFiles) =>
				prevFiles.map((f) =>
					f.id === fileId ? { ...f, status: FileStatusEnum.CONVERTING } : f
				)
			);

			try {
				// Convert and compress the file
				const [convertedFile] = await convertAndCompressImages(
					[file.file as File],
					t,
					false
				);

				// Set status to UPLOADING
				setLocalFile((prevFiles) =>
					prevFiles.map((f) =>
						f.id === fileId
							? { ...f, progress: 0, status: FileStatusEnum.UPLOADING }
							: f
					)
				);

				const formData = new FormData();
				formData.append('reqUploadDocumentList[0].mutipartFile', convertedFile);
				formData.append(
					'reqUploadDocumentList[0].fileType',
					FileTypeEnum.UPLOAD
				);

				const response = await uploadFileAPI(formData, (progressEvent) => {
					if (progressEvent.total) {
						const percentCompleted = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total
						);
						setLocalFile((prevFiles) =>
							prevFiles.map((f) =>
								f.id === fileId
									? {
											...f,
											progress: Math.min(percentCompleted, 99),
										}
									: f
							)
						);
					}
				});

				if (!response?.data?.length) {
					throw new Error('Failed to upload file');
				}

				// Set status to DONE
				setLocalFile((prevFiles) =>
					prevFiles.map((f) =>
						f.id === fileId
							? {
									...f,
									...response?.data[0],
									status: FileStatusEnum.DONE,
									progress: 100,
									file: null,
								}
							: f
					)
				);
			} catch (error) {
				// Set status to ERROR
				setLocalFile((prevFiles) =>
					prevFiles.map((f) =>
						f.id === fileId
							? {
									...f,
									status: FileStatusEnum.ERROR,
									progress: 0,
								}
							: f
					)
				);
			}
		},
		[t]
	);

	const uploadSequentially = useCallback(
		async (filesToUpload: LocalFile[]) => {
			for (const file of filesToUpload) {
				await uploadSingleFile(file);
			}
		},
		[uploadSingleFile]
	);

	const onClickUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.files == null) return;
			const filesArray = Array.from(e.target.files);

			if (
				filesArray.some(
					(file) => !validateAllowFile(file, REGEX.ALLOW_UPLOAD_FILES)
				)
			)
				return;

			if (filesArray.length > MAX_FILE_UPLOAD) {
				showAlert(ALERT_38(MAX_FILE_UPLOAD));
				return;
			}

			if (
				filesArray.some(
					(file) =>
						getImageType(file).isPDFFile && file.size > FILE_SIZE_10MB_BYTE
				)
			) {
				showAlert(ALERT_39(FILE_SIZE_10MB));
				return;
			}

			setConverting(true);
			const newFiles = formatLocalFile(localFiles, filesArray);

			// Reset originalFileNames for new batch and store new file names
			const newOriginalFileNames = newFiles.reduce(
				(acc, file) => {
					acc[file.id] = file.file?.name || '';
					return acc;
				},
				{} as Record<string, string>
			);

			setOriginalFileNames(newOriginalFileNames); // Reset and set new names
			setLocalFile((prev) => [...newFiles, ...prev]);
			setConverting(false);

			// Start sequential upload
			uploadSequentially(newFiles);

			e.target.value = '';
		},
		[localFiles, uploadSequentially]
	);

	const onDeleteLocalFile = useCallback(() => {
		if (isNilOrEmpty(fileRowSelection.selectedRowKeys)) {
			showAlert('삭제할 업로드 목록를(을) 선택해주세요.');
			return;
		}

		const newLocalFiles = localFiles.filter(
			(item) => !includes(item.id, fileRowSelection.selectedRowKeys)
		);
		setLocalFile(newLocalFiles);
		fileRowSelection.onReset();
	}, [fileRowSelection.selectedRowKeys, localFiles]);

	// Check for HEIC/HEIF files after all uploads are complete
	const allCompleted = useMemo(() => {
		return (
			localFiles.length > 0 &&
			localFiles.every(
				(file) =>
					file.status === FileStatusEnum.DONE ||
					file.status === FileStatusEnum.ERROR
			)
		);
	}, [localFiles]);

	useEffect(() => {
		if (allCompleted) {
			const hasHeicHeif = Object.values(originalFileNames).some((fileName) =>
				isHeicHeifFile(fileName)
			);
			if (hasHeicHeif) {
				showAlert(t('has-heic-heif-need-convert-format'));
			}
		}
	}, [allCompleted, t, originalFileNames]);

	return {
		fileUploadedDataSource,
		onClickUpload,
		uploadColumns,
		fileRowSelection,
		onDeleteLocalFile,
		setLocalFile,
		converting,
	};
};
